import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomModal } from '../../models/CustomModal.model';
import { DynamicComponentComponent } from '../dynamic-component/dynamic-component.component';
import { TemplateService } from '../../services/template/template.service';
import { Subscription } from 'rxjs';
import { CustomComponent } from '../../models/CustomComponent.model';
import {
  MODE_FALLBACK_ORDER,
  ResponsiveMode,
} from '../../constants/responsive-modes';
import { ModalService } from '../../services/modal/modal.service';
import { InjectionToken } from '@angular/core';

export const MODAL_UUID = new InjectionToken<string>('MODAL_UUID');
export const MODAL_DATA = new InjectionToken<any>('MODAL_DATA');

@Component({
  selector: 'app-dynamic-modal',
  standalone: true,
  imports: [CommonModule, DynamicComponentComponent],
  templateUrl: './dynamic-modal.component.html',
  styleUrls: ['./dynamic-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: MODAL_UUID,
      useFactory: function (comp: DynamicModalComponent) {
        return comp.modal?.uuid;
      },
      deps: [DynamicModalComponent],
    },
    {
      provide: MODAL_DATA,
      useFactory: function (comp: DynamicModalComponent) {
        return comp.data;
      },
      deps: [DynamicModalComponent],
    },
  ],
})
export class DynamicModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() modal: CustomModal | null = null;
  @Input() currentMode: 1 | 2 | 3 = 1;
  @Input() data: any;
  @ViewChild('modalContent') modalContent!: ElementRef;

  private modeSubscription: Subscription;
  private cachedComponents: CustomComponent[] = [];
  private lastMode: number | null = null;

  constructor(
    private el: ElementRef,
    private templateService: TemplateService,
    private modalService: ModalService
  ) {
    this.modeSubscription = new Subscription();
  }

  ngOnInit() {

    if (this.data) {
    }

    this.modeSubscription = this.templateService.currentMode$.subscribe(
      (mode) => {
        this.currentMode = mode;
        this.updateModalDimensions();
      }
    );

    this.updateModalDimensions();

    // Add scroll handling for better UX
    this.setupModalScrolling();
  }

  ngOnDestroy() {
    if (this.modeSubscription) {
      this.modeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentMode'] || changes['modal']) {
      this.updateModalDimensions();
    }
  }

  private updateModalDimensions() {
    if (!this.modal) return;

    const width = this.modal.settings['width']?.value[this.currentMode];
    const height = this.modal.settings['height']?.value[this.currentMode];

    const modalContent = this.el.nativeElement.querySelector('.modal-content');
    if (modalContent) {
      modalContent.style.width = width === 'initial' ? 'auto' : width;
      modalContent.style.height = height === 'initial' ? 'auto' : height;

      // Ensure proper scrolling behavior
      this.ensureModalScrollability(modalContent as HTMLElement);
    }
  }

  /**
   * Sets up modal scrolling behavior for better user experience
   */
  private setupModalScrolling(): void {
    // Wait for DOM to be ready
    setTimeout(() => {
      const modalContent =
        this.el.nativeElement.querySelector('.modal-content');
      if (modalContent) {
        this.ensureModalScrollability(modalContent as HTMLElement);
      }
    }, 100);
  }

  /**
   * Ensures the modal content is properly scrollable
   */
  private ensureModalScrollability(modalContent: HTMLElement): void {
    try {
      // Set proper CSS for scrolling
      modalContent.style.overflowY = 'auto';
      modalContent.style.overflowX = 'hidden';
      modalContent.style.maxHeight = '90vh'; // Prevent modal from being taller than viewport
      modalContent.style.position = 'relative';

      // Add smooth scrolling
      modalContent.style.scrollBehavior = 'smooth';

      // Ensure the modal content can be scrolled programmatically
      if (!modalContent.scrollTo) {
        // Polyfill for older browsers
        (modalContent as any).scrollTo = function (options: any) {
          if (options && typeof options.top === 'number') {
            modalContent.scrollTop = options.top;
          }
        };
      }

    } catch (error) {
      console.error('[DynamicModal] Error configuring modal scrolling:', error);
    }
  }

  /**
   * Scrolls the modal to the top
   * This can be called from child components (like dynamic-form) after form submission
   */
  public scrollToTop(): void {
    try {
      const host = this.el.nativeElement as HTMLElement;
      const targets = [
        host.querySelector('.modal-body'),
        host.querySelector('.modal-content'),
        host,
      ].filter(Boolean) as HTMLElement[];

      const seen = new Set<HTMLElement>();
      for (const target of targets) {
        if (seen.has(target)) {
          continue;
        }
        seen.add(target);
        this.scrollElementToTop(target);
      }
    } catch (error) {
      console.error('[DynamicModal] Error scrolling to top:', error);
    }
  }

  private scrollElementToTop(element: HTMLElement): void {
    if (typeof element.scrollTo === 'function') {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      element.scrollTop = 0;
    }
  }

  private getFallbackComponents(mode: 1 | 2 | 3): CustomComponent[] {
    if (!this.modal) return [];

    // First try to get components for the current mode
    const componentsForMode = this.modal.customComponents.filter(
      (comp) => comp.mode === mode
    );
    if (componentsForMode.length > 0) {
      return componentsForMode;
    }

    // Try each fallback mode in order
    for (const fallbackMode of MODE_FALLBACK_ORDER[mode]) {
      const fallbackComponents = this.modal.customComponents.filter(
        (comp) => comp.mode === fallbackMode
      );
      if (fallbackComponents.length > 0) {
        return fallbackComponents;
      }
    }

    return [];
  }

  isAutoHeight(component: CustomComponent | null | undefined): boolean {
    return !!component?.getOrSetInputs()?.['autoHeight']?.value;
  }

  hasAutoHeightComponents(): boolean {
    return this.getModalComponents().some((component) =>
      this.isAutoHeight(component)
    );
  }

  getModalComponents(): CustomComponent[] {
    if (!this.modal) return [];

    // Return cached components if mode hasn't changed
    if (this.lastMode !== this.currentMode) {
      this.lastMode = this.currentMode;
      this.cachedComponents = this.getFallbackComponents(this.currentMode);
    }

    // Always inject modal data when present (ensures prefilled data is available on first render and when data updates)
    if (this.data) {
      this.cachedComponents.forEach((component) => {
        if (!component.inputs) {
          component.inputs = {};
        }
        component.inputs['modalData'] = { type: 'object', value: this.data };
      });
    }

    return this.cachedComponents;
  }

  closeModal() {
    if (this.modal?.uuid) {
      this.modalService.closeModal(this.modal.uuid);
    }
  }
}
